import {DocumentData, Timestamp} from "firebase/firestore";
import {dateTimeToString} from "../../../tools";
import {CalendarChangeLogType} from "./CalendarChangeLogType";

type FieldDefinition = {
    key: string;
    changeType: CalendarChangeLogType;
    format: (value: unknown) => string;
    reportField?: boolean;
};

export function asCalendarDate(value: unknown): Date | null {
    if (value instanceof Timestamp) return value.toDate();
    if (value instanceof Date) return value;
    return null;
}

export function formatCalendarDate(value: unknown): string {
    return dateTimeToString(asCalendarDate(value)) || "—";
}

export function formatCalendarDeletionSummary(data: DocumentData, eventName?: string): string {
    const employee = typeof data.employeeName === "string" ? data.employeeName : "Неизвестен служител";
    const resolvedEventName = eventName || (typeof data.eventId === "string" ? data.eventId : "Без събитие");
    const parts = [
        employee,
        resolvedEventName,
        `${formatCalendarDate(data.eventDayStart)} – ${formatCalendarDate(data.eventDayEnd)}`,
    ];

    if (typeof data.restTime === "number" && data.restTime !== 0) {
        parts.push(`Почивка: ${formatNumber(data.restTime, " мин.")}`);
    }
    if (typeof data.bonus === "number" && data.bonus !== 0) {
        parts.push(`Бонус: ${formatNumber(data.bonus, " €")}`);
    }

    return parts.join("; ");
}

function formatText(value: unknown): string {
    const text = typeof value === "string" ? value.trim() : "";
    return text || "(празно)";
}

function formatBoolean(value: unknown): string {
    return value === true ? "Да" : "Не";
}

function formatNumber(value: unknown, suffix: string): string {
    return `${typeof value === "number" ? value : 0}${suffix}`;
}

function formatDayType(value: unknown): string {
    switch (value) {
        case "WORKDAY": return "Работен ден";
        case "HOLIDAY": return "Планирана отпуска";
        case "SICK": return "Болничен";
        case "DAYOFF": return "Друг неработен ден";
        default: return formatText(value);
    }
}

function formatFace(value: unknown): string {
    switch (value) {
        case "GOOD": return "Положителна";
        case "NEUTRAL": return "Неутрална";
        case "BAD": return "Негативна";
        default: return "Без оценка";
    }
}

const FIELD_DEFINITIONS: FieldDefinition[] = [
    {key: "eventId", changeType: "EVENT", format: formatText},
    {key: "eventDayStart", changeType: "START_DATE", format: formatCalendarDate},
    {key: "dayType", changeType: "DAY_TYPE", format: formatDayType},
    {key: "description", changeType: "DESCRIPTION", format: formatText},
    {key: "isSupervisor", changeType: "IS_SUPERVISOR", format: formatBoolean},
    {key: "isWarehouse", changeType: "IS_WAREHOUSE", format: formatBoolean},
    {key: "isMontage", changeType: "IS_MONTAGE", format: formatBoolean},
    {key: "eventDayEnd", changeType: "END_DATE", format: formatCalendarDate, reportField: true},
    {key: "restTime", changeType: "REST_TIME", format: value => formatNumber(value, " мин."), reportField: true},
    {key: "bonus", changeType: "BONUS", format: value => formatNumber(value, " €"), reportField: true},
    {key: "face", changeType: "FACE", format: formatFace, reportField: true},
    {key: "notes", changeType: "NOTES", format: formatText, reportField: true},
    {key: "isConfirmed", changeType: "IS_CONFIRMED", format: formatBoolean, reportField: true},
];

function valuesAreEqual(first: unknown, second: unknown): boolean {
    const firstDate = asCalendarDate(first);
    const secondDate = asCalendarDate(second);
    if (firstDate || secondDate) return firstDate?.getTime() === secondDate?.getTime();
    return (first ?? null) === (second ?? null);
}

function hasMeaningfulPreviousValue(value: unknown): boolean {
    if (value === null || value === undefined) return false;
    if (typeof value === "string") return value.trim().length > 0 && value !== "NOT SET";
    return true;
}

export function getCalendarFieldChanges(current: DocumentData, updated: DocumentData): Array<{
    changeType: CalendarChangeLogType;
    previousValue: string;
    newValue: string;
}> {
    const reportWasInitialized = asCalendarDate(current.eventDayEnd) !== null;

    return FIELD_DEFINITIONS.flatMap(definition => {
        if (!Object.prototype.hasOwnProperty.call(updated, definition.key)) return [];
        const previous = current[definition.key];
        const next = updated[definition.key];
        if (valuesAreEqual(previous, next)) return [];
        if (definition.reportField && !reportWasInitialized) return [];
        if (!definition.reportField && !hasMeaningfulPreviousValue(previous)) return [];

        return [{
            changeType: definition.changeType,
            previousValue: definition.format(previous),
            newValue: definition.format(next),
        }];
    });
}
